import { Grid } from "@material-ui/core";
import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useStore } from "store/StoreConfigs";
import clsx from "clsx";
import { laneViewStyles } from "./LaneViewStyles";
import { dateEndFormatting, dateStartFormatting } from "helpers/dateFormatting";
import AdvancedFilters from "components/AdvancedFilters/AdvancedFilters";
import { LaneViewTotalsWidgetsWrapper } from "./LaneViewTotalsWidgetsWrapper";
import { LaneRequestParameters } from "api/lokiRequestModels/LaneRequestParameters";
import { useParams } from "react-router";
import { LaneViewRoutesTableWrapper } from "./LaneViewRoutesTableWrapper";
import { LaneViewMap } from "components/LaneViewMap/LaneViewMap";
import { TempProfileChart } from "components/TempProfileChart/TempProfileChart";
import moment from "moment";
import StatisticsComponent from "components/StatisticsComponent/StatisticsComponent";
import DynamicGridTitle from "components/GridTitle/DynamicGridTitle";
import { LaneViewShipmentsTableWrapper } from "./LaneViewShipmentsTableWrapper";

export default observer(function LaneView() {
    const classes = laneViewStyles();
    const { commonStore, laneStore, globalViewStore, filtersStore, identityStore } = useStore();
    const [isFiltersReset, setResetFilters] = useState(false);
    const { laneId } = useParams<any>();

    const laneRequest: LaneRequestParameters = {
        // TODO: move these into filters store to reduce duplication
        startTime: dateStartFormatting(filtersStore.startDate),
        endTime: dateEndFormatting(filtersStore.endDate),
        customerId: identityStore.currentCustomerId,
        laneId,
        lsp: filtersStore.selectedLogisticsServiceProviders.join(",") || undefined,
        productType: filtersStore.selectedProductTypes.join(",") || undefined,
        riskLevel: filtersStore.selectRiskLevels.join(",") || undefined,
        transportationModes: filtersStore.selectedModesOfTransportation.join(",") || undefined,
        packagingType: filtersStore.selectedPackagingTypes.join(",") || undefined,
        sort: laneStore.sortRoute,
        direction: laneStore.directionRoute,
    };

    const shipmentRequest: LaneRequestParameters = {
        // TODO: move these into filters store to reduce duplication
        startTime: dateStartFormatting(filtersStore.startDate),
        endTime: dateEndFormatting(filtersStore.endDate),
        customerId: identityStore.currentCustomerId,
        laneId,
        lsp: filtersStore.selectedLogisticsServiceProviders.join(",") || undefined,
        productType: filtersStore.selectedProductTypes.join(",") || undefined,
        riskLevel: filtersStore.selectRiskLevels.join(",") || undefined,
        transportationModes: filtersStore.selectedModesOfTransportation.join(",") || undefined,
        packagingType: filtersStore.selectedPackagingTypes.join(",") || undefined,
        sort: laneStore.sortShipment,
        direction: laneStore.directionShipment,
    };

    const callbackRequest = () => {
        if (filtersStore.startDate === null || filtersStore.endDate === null) {
            return;
        }

        laneStore.loadLaneTemperatureStatistics(laneRequest);
        laneStore.loadLaneTripStatistics(laneRequest);
        laneStore.loadRouteAggregations(laneRequest);
        laneStore.loadProductExcursionsList(laneRequest);
        commonStore.setFiltersClose();
        commonStore.setLaneRoutesSelected();
    };

    const AdvancedFiltersRequest = () => {
        if (filtersStore.startDate === null || filtersStore.endDate === null) {
            return;
        }
        laneStore.loadLaneTemperatureStatistics(laneRequest);
        laneStore.loadLaneTripStatistics(laneRequest);
        laneStore.loadRouteAggregations(laneRequest);
        laneStore.loadProductExcursionsList(laneRequest);
        laneStore.loadFirstRoutesPage(laneRequest);
        laneStore.loadFirstShipmentsPage(shipmentRequest);
        commonStore.setFiltersClose();
        commonStore.setLaneRoutesSelected();
    };

    useEffect(() => {
        isFiltersReset ? setResetFilters(true) : filtersStore.resetFilters();

        callbackRequest();
        window.scrollTo(0, 0);
    }, [filtersStore.startDate, filtersStore.endDate]);

    useEffect(() => {
        if (filtersStore.startDate === null || filtersStore.endDate === null) {
            return;
        }
        laneStore.loadFirstRoutesPage(laneRequest);
        commonStore.setFiltersClose();
    }, [laneStore.directionRoute, laneStore.sortRoute, filtersStore.startDate, filtersStore.endDate]);

    useEffect(() => {
        if (filtersStore.startDate === null || filtersStore.endDate === null) {
            return;
        }
        laneStore.loadFirstShipmentsPage(shipmentRequest);
        commonStore.setFiltersClose();
    }, [laneStore.directionShipment, laneStore.sortShipment, filtersStore.startDate, filtersStore.endDate]);

    useEffect(() => {
        globalViewStore.setSelectedLane(laneId);
        commonStore.setCurrentView("lane");
    }, []);

    useEffect(() => {
        laneStore.loadLaneTemperatureProfile(laneRequest);
    }, []);

    return (
        <>
            <AdvancedFilters requestCallback={AdvancedFiltersRequest} />

            <div
                className={clsx({
                    [classes.dis]: commonStore.isFilterOpen,
                })}
            ></div>

            <main className={classes.content}>
                <div className={classes.toolbar} />

                <div className={classes.section}>
                    <LaneViewMap />
                </div>
                <LaneViewTotalsWidgetsWrapper />

                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    className={classes.gridMargin}
                >
                    <Grid item>
                        <StatisticsComponent
                            button={true}
                            unit={commonStore.isCelsiusShowed ? laneStore.unitCelsius : laneStore.unitFahrenheit}
                            title={"TEMPERATURE PROFILE"}
                            min={
                                commonStore.isCelsiusShowed
                                    ? laneStore.minTempF === null
                                        ? "No Data"
                                        : laneStore.minTempC.toFixed(2)
                                    : laneStore.minTempF === null
                                    ? "No Data"
                                    : laneStore.minTempF.toFixed(2)
                            }
                            max={
                                commonStore.isCelsiusShowed
                                    ? laneStore.maxTempF === null
                                        ? "No Data"
                                        : laneStore.maxTempC.toFixed(2)
                                    : laneStore.maxTempF === null
                                    ? "No Data"
                                    : laneStore.maxTempF.toFixed(2)
                            }
                            mean={
                                commonStore.isCelsiusShowed
                                    ? laneStore.meanTempF === null
                                        ? "No Data"
                                        : laneStore.meanTempC.toFixed(2)
                                    : laneStore.meanTempF === null
                                    ? "No Data"
                                    : laneStore.meanTempF.toFixed(2)
                            }
                            stdDev={
                                commonStore.isCelsiusShowed
                                    ? laneStore.stdDevTempF === null
                                        ? "No Data"
                                        : laneStore.stdDevTempC.toFixed(2)
                                    : laneStore.stdDevTempF === null
                                    ? "No Data"
                                    : laneStore.stdDevTempF.toFixed(2)
                            }
                            isLoading={laneStore.loadingStatistics}
                        ></StatisticsComponent>
                    </Grid>
                    <Grid item>
                        <StatisticsComponent
                            title={"SHIPMENT DURATION"}
                            subTitle={"(hours)"}
                            min={laneStore.minTrip === null ? "No data" : `${laneStore.minTrip.toFixed(2)} `}
                            max={laneStore.maxTrip === null ? "No data" : `${laneStore.maxTrip.toFixed(2)} `}
                            mean={laneStore.meanTrip === null ? "No data" : `${laneStore.meanTrip.toFixed(2)} `}
                            stdDev={laneStore.stdDevTrip === null ? "No data" : `${laneStore.stdDevTrip.toFixed(2)}`}
                            isLoading={laneStore.loadingStatistics}
                        ></StatisticsComponent>
                    </Grid>
                </Grid>

                <Grid container className={classes.section}>
                    <Grid item xs={6}>
                        <TempProfileChart
                            minTimestamp={moment(filtersStore.startDate).valueOf()}
                            maxTimestamp={moment(filtersStore.endDate).valueOf()}
                            tempItems={laneStore.laneTempProfile}
                            isLoading={laneStore.loadingLaneTemperatureProfile}
                        />
                    </Grid>
                </Grid>

                <div className={classes.section}>
                    <DynamicGridTitle
                        firstTitle={"Routes"}
                        secondTitle={"Shipments"}
                        firstStyle={commonStore.isRoutesSelected}
                        secondStyle={commonStore.isLaneShipmentsSelected}
                        firstOnClick={commonStore.setLaneRoutesSelected}
                        secondOnClick={commonStore.setLaneShipmentsSelected}
                        subText={"Keep an eye on the latest route risk level changes."}
                    />
                    {commonStore.isLaneShipmentsSelected && <LaneViewShipmentsTableWrapper />}
                    {commonStore.isRoutesSelected && <LaneViewRoutesTableWrapper />}
                </div>
            </main>
        </>
    );
});
